import { useMemo } from "react"

import type { AnimationEvent } from "@/types"

/** 1 = first half, 2 = second half, "et" = extra time. */
export type HalfTag = 1 | 2 | "et"

/**
 * A contiguous group of animation events belonging to the same turn and team.
 */
export interface TurnGroup {
  /** Turn label string (e.g. "Turn 1", "Kickoff", null → "Play"). */
  turnLabel: string
  /** Acting team ID for this group, or null for neutral phases (kickoff, etc.). */
  teamId: string | null
  /** Index in the events array where this group starts (inclusive). */
  startIndex: number
  /** Index in the events array where this group ends (inclusive). */
  endIndex: number
  /** Which half this group belongs to (1, 2, or "et" for extra time). */
  half: HalfTag
}

/**
 * Pure function: groups animation events into turn boundary segments.
 * A new group starts whenever `turnLabel` or `actingTeamId` changes.
 * Half tracking: prefer the explicit `phase` field on each event; fall back
 * to counting kickoff events for older replays without phase data.
 *
 * Exported for unit testing.
 */
export function computeTurnGroups(events: AnimationEvent[]): TurnGroup[] {
  if (events.length === 0) return []

  const groups: TurnGroup[] = []
  let currentGroup: TurnGroup | null = null
  let kickoffCount = 0

  for (let i = 0; i < events.length; i++) {
    const event = events[i]

    // Track kickoff events as a fallback for older replays without phase data
    if (event.highlightType === "kickoff") {
      kickoffCount++
    }

    const half = halfTagForEvent(event, kickoffCount)
    const turnLabel = event.turnLabel ?? "Play"
    const teamId = event.actingTeamId ?? null

    const isSameGroup =
      currentGroup !== null &&
      currentGroup.turnLabel === turnLabel &&
      currentGroup.teamId === teamId &&
      currentGroup.half === half

    if (isSameGroup && currentGroup) {
      // Extend the current group
      currentGroup.endIndex = i
    } else {
      // Start a new group
      currentGroup = {
        turnLabel,
        teamId,
        startIndex: i,
        endIndex: i,
        half,
      }
      groups.push(currentGroup)
    }
  }

  return groups
}

function halfTagForEvent(event: AnimationEvent, kickoffCount: number): HalfTag {
  switch (event.phase) {
    case "first_half":
      return 1
    case "second_half":
      return 2
    case "extra_time":
      return "et"
    default:
      return kickoffCount >= 2 ? 2 : 1
  }
}

/**
 * Memoized hook that returns turn groups and the index of the current group.
 */
export function useTurnGroups(
  events: AnimationEvent[],
  currentEventIndex: number,
): {
  turnGroups: TurnGroup[]
  currentGroupIndex: number
} {
  const turnGroups = useMemo(() => computeTurnGroups(events), [events])

  const currentGroupIndex = useMemo(() => {
    // Binary-search-style: find which group contains currentEventIndex
    for (let g = 0; g < turnGroups.length; g++) {
      const group = turnGroups[g]
      if (
        currentEventIndex >= group.startIndex &&
        currentEventIndex <= group.endIndex
      ) {
        return g
      }
    }
    // Fallback: last group
    return Math.max(0, turnGroups.length - 1)
  }, [turnGroups, currentEventIndex])

  return { turnGroups, currentGroupIndex }
}
